import React, { createContext, useContext, useState } from "react";
import reportService from "../api/services/reportService";

// Create context
const ReportContext = createContext();

// Custom hook for using this context
export const useReport = () => useContext(ReportContext);

// Provider component
export const ReportProvider = ({ children }) => {
  // State for profit and loss report
  const [profitLoss, setProfitLoss] = useState(null);
  const [loadingProfitLoss, setLoadingProfitLoss] = useState(false);
  const [profitLossError, setProfitLossError] = useState(null);

  // State for balance sheet report
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loadingBalanceSheet, setLoadingBalanceSheet] = useState(false);
  const [balanceSheetError, setBalanceSheetError] = useState(null);

  // State for cashflow report
  const [cashflow, setCashflow] = useState(null);
  const [loadingCashflow, setLoadingCashflow] = useState(false);
  const [cashflowError, setCashflowError] = useState(null);

  // State for customer aging report
  const [customerAging, setCustomerAging] = useState(null);
  const [loadingCustomerAging, setLoadingCustomerAging] = useState(false);
  const [customerAgingError, setCustomerAgingError] = useState(null);

  // State for vendor aging report
  const [vendorAging, setVendorAging] = useState(null);
  const [loadingVendorAging, setLoadingVendorAging] = useState(false);
  const [vendorAgingError, setVendorAgingError] = useState(null);

  // State for dashboard summary
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Load profit and loss report
  const loadProfitLoss = async (params = {}) => {
    setLoadingProfitLoss(true);
    setProfitLossError(null);
    try {
      const data = await reportService.getProfitLoss(params);
      setProfitLoss(data);
      return data;
    } catch (error) {
      console.error("Error loading profit and loss report:", error);
      setProfitLossError("Failed to load profit and loss report");
      throw error;
    } finally {
      setLoadingProfitLoss(false);
    }
  };

  // Load balance sheet report
  const loadBalanceSheet = async (params = {}) => {
    setLoadingBalanceSheet(true);
    setBalanceSheetError(null);
    try {
      const data = await reportService.getBalanceSheet(params);
      setBalanceSheet(data);
      return data;
    } catch (error) {
      console.error("Error loading balance sheet report:", error);
      setBalanceSheetError("Failed to load balance sheet report");
      throw error;
    } finally {
      setLoadingBalanceSheet(false);
    }
  };

  // Load cashflow report
  const loadCashflow = async (params = {}) => {
    setLoadingCashflow(true);
    setCashflowError(null);
    try {
      const data = await reportService.getCashflow(params);
      setCashflow(data);
      return data;
    } catch (error) {
      console.error("Error loading cashflow report:", error);
      setCashflowError("Failed to load cashflow report");
      throw error;
    } finally {
      setLoadingCashflow(false);
    }
  };

  // Load customer aging report
  const loadCustomerAging = async (params = {}) => {
    setLoadingCustomerAging(true);
    setCustomerAgingError(null);
    try {
      const data = await reportService.getCustomerAging(params);
      setCustomerAging(data);
      return data;
    } catch (error) {
      console.error("Error loading customer aging report:", error);
      setCustomerAgingError("Failed to load customer aging report");
      throw error;
    } finally {
      setLoadingCustomerAging(false);
    }
  };

  // Load vendor aging report
  const loadVendorAging = async (params = {}) => {
    setLoadingVendorAging(true);
    setVendorAgingError(null);
    try {
      const data = await reportService.getVendorAging(params);
      setVendorAging(data);
      return data;
    } catch (error) {
      console.error("Error loading vendor aging report:", error);
      setVendorAgingError("Failed to load vendor aging report");
      throw error;
    } finally {
      setLoadingVendorAging(false);
    }
  };

  // Load dashboard summary
  const loadDashboardSummary = async () => {
    setLoadingSummary(true);
    setSummaryError(null);
    try {
      const data = await reportService.getDashboardSummary();
      setDashboardSummary(data);
      return data;
    } catch (error) {
      console.error("Error loading dashboard summary:", error);
      setSummaryError("Failed to load dashboard summary");
      throw error;
    } finally {
      setLoadingSummary(false);
    }
  };

  // Context value to be provided
  const value = {
    // Profit and Loss data and methods
    profitLoss,
    loadingProfitLoss,
    profitLossError,
    loadProfitLoss,

    // Balance Sheet data and methods
    balanceSheet,
    loadingBalanceSheet,
    balanceSheetError,
    loadBalanceSheet,

    // Cashflow data and methods
    cashflow,
    loadingCashflow,
    cashflowError,
    loadCashflow,

    // Customer Aging data and methods
    customerAging,
    loadingCustomerAging,
    customerAgingError,
    loadCustomerAging,

    // Vendor Aging data and methods
    vendorAging,
    loadingVendorAging,
    vendorAgingError,
    loadVendorAging,

    // Dashboard Summary data and methods
    dashboardSummary,
    loadingSummary,
    summaryError,
    loadDashboardSummary,
  };

  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  );
};

export default ReportContext;
